import { Type, TSchema } from "@sinclair/typebox";
import SchemaValidator from "../schema-validator";
import { OptionalOrNull } from "../shared-schemas";

function PaginatedResults<T extends TSchema>(type: T) {
	return Type.Object({
		currentPage: Type.Number(),
		totalElements: Type.Number(),
		totalPages: Type.Number(),
		results: Type.Array(type),
	});
}

const liveSession = new SchemaValidator(
	Type.Object({
		liveSessionId: Type.String(),
	}),
);

const proctorGroup = Type.Object({
	id: Type.Number(),
	name: Type.String(),
	ownerInstituteId: Type.String(),
	createdAt: Type.String(),
	updatedAt: Type.String(),
});

const liveSessionUser = Type.Object({
	liveSessionId: Type.String(),
	userId: Type.String(),
	role: Type.Union([Type.Literal("proctor"), Type.Literal("student")]),
	hasJoined: Type.Boolean(),
	createdAt: Type.String(),
	updatedAt: Type.String(),
});
const liveSessionUserValidator = new SchemaValidator(liveSessionUser);

const liveSessionUserSession = Type.Object({
	id: Type.String(),
	userId: Type.String(),
	liveSessionId: Type.String(),
	role: Type.Union([Type.Literal("proctor"), Type.Literal("student")]),
	connectionState: Type.Union([
		Type.Literal("connecting"),
		Type.Literal("connected"),
		Type.Literal("reconnecting"),
		Type.Literal("failed"),
	]),
});
const liveSessionUserSessionValidator = new SchemaValidator(liveSessionUserSession);

const liveSessionInvitation = Type.Object({
	liveSessionId: Type.String(),
	proctorGroupId: Type.Number(),
	state: Type.Object({
		selectedProctorId: OptionalOrNull(Type.String()),
		firstJoinedAt: OptionalOrNull(Type.String()),
		cancelledAt: OptionalOrNull(Type.String()),
	}),
	createdAt: Type.String(),
	updatedAt: Type.String(),
});
const liveSessionInvitations = new SchemaValidator(Type.Array(liveSessionInvitation));

const liveSessionNote = Type.Object({
	id: Type.String(),
	liveSessionId: Type.String(),
	student: Type.String(),
	edits: Type.Array(
		Type.Object({
			id: Type.String(),
			editor: Type.String(),
			content: Type.String(),
			timestamp: Type.Number(),
		}),
	),
});

const liveSessionNotes = new SchemaValidator(Type.Array(liveSessionNote));

const assessmentLiveSession = {
	id: Type.String(),
	instituteId: OptionalOrNull(Type.String()),
	courseId: OptionalOrNull(Type.String()),
	assessmentId: OptionalOrNull(Type.String()),
	name: Type.String(),
	startTime: Type.String(),
	proctorGuide: OptionalOrNull(Type.String()),
	endTime: Type.String(),
	capacity: Type.Number(),
	registrationStartTime: OptionalOrNull(Type.String()),
	registrationEndTime: OptionalOrNull(Type.String()),
	createdAt: Type.String(),
	updatedAt: Type.String(),
};
const liveSessionWithUsers = Type.Object({
	...assessmentLiveSession,
	users: Type.Array(liveSessionUser),
});

const baseAssessmentLiveSession = new SchemaValidator(Type.Object(assessmentLiveSession));

const singleLiveSession = new SchemaValidator(liveSessionWithUsers);

const liveSessionList = new SchemaValidator(PaginatedResults(Type.Object(assessmentLiveSession)));

const liveSessionListWithUsers = new SchemaValidator(PaginatedResults(liveSessionWithUsers));

const courseLiveProctorInfo = new SchemaValidator(
	Type.Array(
		Type.Object({
			id: Type.Number(),
			uuid: Type.String(),
			name: Type.String(),
			email: OptionalOrNull(Type.String()),
		}),
	),
);

const instituteProctorGroups = new SchemaValidator(
	PaginatedResults(
		Type.Intersect([
			proctorGroup,
			Type.Object({
				allowedInstitutes: Type.Array(
					Type.Object({
						instituteId: Type.String(),
						minHoursNotice: Type.Number(),
						trackCancellationHoursBefore: Type.Number(),
						trackCancellationMessage: Type.String(),
						autoCancelEmptyHoursBefore: OptionalOrNull(Type.Number()),
					}),
				),
			}),
		]),
	),
);

const registerLiveSessionInstituteUser = new SchemaValidator(liveSessionUser);

const deregisterLiveSessionInstituteUser = new SchemaValidator(liveSessionUser);

const registerLiveSessionProctorGroup = new SchemaValidator(OptionalOrNull(liveSessionInvitation));

const deregisterLiveSessionProctorGroup = new SchemaValidator(liveSessionInvitation);

const liveSessionInvitationValidator = new SchemaValidator(liveSessionInvitation);

const liveSessionLiveState = new SchemaValidator(
	Type.Object({
		id: Type.String(),
		userSessionIds: Type.Array(Type.String()),
	}),
);

const liveProctorGroup = new SchemaValidator(proctorGroup);
const liveProctorGroups = new SchemaValidator(Type.Array(proctorGroup));

export default {
	liveSession,
	singleLiveSession,
	liveSessionUser: liveSessionUserValidator,
	liveSessionUserSession: liveSessionUserSessionValidator,
	liveSessionInvitation: liveSessionInvitationValidator,
	liveSessionList,
	liveSessionListWithUsers,
	liveSessionInvitations,
	courseLiveProctorInfo,
	instituteProctorGroups,
	liveSessionNotes,
	baseAssessmentLiveSession,
	registerLiveSessionInstituteUser,
	deregisterLiveSessionInstituteUser,
	registerLiveSessionProctorGroup,
	deregisterLiveSessionProctorGroup,
	liveSessionLiveState,
	liveProctorGroup,
	liveProctorGroups,
};
